import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import theme, { themeColors } from 'themes';
import { JBTColors } from 'common/theme/light.theme';
import {
  InputContainer,
  PhoneContainer
} from 'pages/UserManagementTwo/components/User/AddEditUser/UserForm';
import { useTranslation } from 'react-i18next';
import Input from 'components/Input';
import Select from 'react-select';
import StyledTextArea from 'components/TextArea';
import { Button } from 'components';
import { useGetUserBasicInformationQuery, useUpdateUserBasicInformationMutation } from 'api';
import { UserBasicInformation } from 'types/user-management';
import { ToastMsg } from 'common/components/Toast';
import {
  UserSettingDateFormat,
  UserSettingTimeFormat,
  UserObjectDeepEqual
} from './UserSettingsUtility';
import { toUpper } from 'lodash';
import breakpoints from '../../constants/breakpoints';
import { alphaNumericNameWithSpaceAndHyphen } from 'common/helpers/v1';
import { CountryIso2, PhoneInput } from 'react-international-phone';
import CustomLoader from 'components/CustomLoader';
import { isValidPhoneNumber } from 'libphonenumber-js';

const size = breakpoints.size;
const Container = styled.div<{ isActive?: boolean }>`
  display: ${({ isActive }) => (isActive ? 'flex' : 'none')};
  flex-direction: column;
  height: 100%;
`;
export const HeaderBox = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${JBTColors.mainDisabled};
  margin-bottom: 1rem;
`;
const BioDataBox = styled.div`
  height: calc(100vh - 25rem);
  padding: 2px;
  overflow: auto;
  display: flex;
  gap: 1.5rem;
  @media (max-width: ${size.xl}px) {
    flex-direction: column;
  }
`;
export const HeaderTitle = styled.div`
  color: ${theme.colors.text.lightBlack};
  font-family: Roboto;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  margin-bottom: 10px;
`;
export const HeaderMessage = styled.div`
  color: #454545;
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
`;
const BioLeftColulmn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const BioRightColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
export const RowItemLabel = styled.label<{ isDisabled?: boolean; marginRight?: string }>`
  align-items: center;
  color: ${(props) =>
    props.isDisabled ? theme.colors.text.mutedgray : theme.colors.text.lightBlack};
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  margin-right: ${(props) => (props.marginRight ? props.marginRight : '0')};
`;
const RowItemContainer = styled(InputContainer)`
  height: 41px;
  margin-right: 40px;
`;
const TextContainer = styled.div`
  with: auto;
  textarea {
    outline: none;
    border: 1px solid #d8dde3;
    border-radius: 4px;
  }
  textarea:focus {
    outline: none !important;
    border: 1px solid ${themeColors.primaryBlue5};
  }
`;
const Mandatory = styled.div`
  color: ${({ theme }) => theme.colors.darkRed};
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  margin-left: 4px;
`;
const RowLabelWrapper = styled.div`
  display: flex;
  margin-bottom: 4px;
`;
const AdjustForHeight = styled.div`
  margin-top: -6px;
`;
export const FormSection = styled.section<{ marginBottom?: string }>`
  display: flex;
  flex-direction: column;
  margin-bottom: ${(props) => (props.marginBottom ? props.marginBottom : '10')}px;

  div[class*='placeholder'],
  select option:first-child {
    color: #5d6a86;
  }

  &.occurrences {
    .ui-widget__main div > div {
      &:first-child {
        width: 24%;
      }
      &:nth-child(2) {
        width: 20%;
        margin: 0 1rem;
      }
      &:last-child {
        width: 56%;
      }
    }
  }

  .ui-widget {
    width: 100%;
  }

  .ui-widget__main > p {
    font-size: 0.875rem;
    padding-left: 1rem;
  }

  .ui-widget__main {
    overflow: visible;
  }

  .ui-date-range-picker {
    max-width: 435px;
  }

  .dropdown_checkboxes {
    font-family: ${({ theme }) => `${theme.typography.family}`};
    font-size: 0.875rem;
  }

  select {
    background: none;
  }
`;
export const SettingsFooter = styled.div``;
export const ButtonWrapper = styled.div`
  position: relative;
  top: -10px;
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;
export const FooterWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding-top: 0;
`;
export const DisClaimer = styled.div`
  display: flex;
  flex-direction: row;
`;
export const DisClaimerRequired = styled.span`
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  color: ${({ theme }) => theme.colors.lightGrey8};
  margin-top: 4px;
  margin-left: 40px;
`;
export const DisclaimerText = styled.span`
  display: flex;
  font-size: 14px;
  font-style: italic;
  font-weight: 400;
  line-height: 16.8px;
`;
export const DisclaimerNote = styled.div`
  font-size: 14px;
  font-weight: 600;
  font-style: normal;
  height: 14px;
  padding-right: 10px;
`;
export const DisClaimerConfirmation = styled.span`
  position: relative;
  top: 1px;
  margin-right: 8px;
  margin-left: 16px;
`;
export const Asterics = styled.span`
  color: ${({ theme }) => theme.colors.darkRed};
  font-size: 12px;
  margin-right: 4px;
`;
export const DisclaimerWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const UserDataForm = styled.form`
  width: 100%;
`;
type Props = {
  isActive?: boolean;
};
export const BasicInformation = ({ isActive }: Props): JSX.Element => {
  const { t } = useTranslation(['common']);
  const [basicInfo, setBasicInfo] = useState<UserBasicInformation | undefined>();
  const [basicInfoTracked, setBasicInfoTracked] = useState<UserBasicInformation | undefined>();
  const [isBasicInfoChanged, setIsBasicInfoChanged] = useState<boolean>(false);
  const { data: info, refetch, isLoading } = useGetUserBasicInformationQuery();
  const [updateBasicInfo] = useUpdateUserBasicInformationMutation();
  useEffect(() => {
    if (info) {
      setBasicInfo(info);
      setBasicInfoTracked(info);
    }
    refetch();
  }, [info, isActive]);
  useMemo(() => {
    setIsBasicInfoChanged(
      !UserObjectDeepEqual<UserBasicInformation>(
        basicInfo as UserBasicInformation,
        basicInfoTracked as UserBasicInformation
      )
    );
  }, [basicInfo, basicInfoTracked]);

  const validatePhone = (phone: string): boolean => {
    let isValid = true;
    if (phone === '') {
      isValid = true;
    } else if (!isValidPhoneNumber(phone)) {
      isValid = false;
    }
    return isValid;
  };

  const updateInfoOnFieldChange = (key: string, value: string) => {
    setBasicInfo((prevState) => {
      if (!prevState) return prevState;
      return {
        ...prevState,
        [key]: value
      } as UserBasicInformation;
    });
  };

  const checkSaveDisable = () => {
    if (isBasicInfoChanged) {
      if (validatePhone(basicInfo?.phone || '')) {
        return false;
      }
      return true;
    }
    return true;
  };

  return (
    <>
      <Container isActive={isActive}>
        <HeaderBox>
          <HeaderTitle>{t('basic_information')}</HeaderTitle>
          <HeaderMessage>{t('you_can_set_up_your_account_preferences')}</HeaderMessage>
        </HeaderBox>
        {isLoading ? (
          <CustomLoader size="3rem" />
        ) : (
          <UserDataForm>
            <BioDataBox>
              <BioLeftColulmn>
                <FormSection marginBottom="18">
                  <RowLabelWrapper>
                    <RowItemLabel>{t('first_name')}</RowItemLabel>
                    <Mandatory>*</Mandatory>
                  </RowLabelWrapper>
                  <RowItemContainer>
                    <Input
                      value={basicInfo?.firstName}
                      onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                        evt.target.value.length <= 35 &&
                          alphaNumericNameWithSpaceAndHyphen(evt.target.value) &&
                          updateInfoOnFieldChange('firstName', evt.target.value as string);
                      }}
                      placeholder={t('first_name')}
                    />
                  </RowItemContainer>
                </FormSection>
                <FormSection marginBottom="18">
                  <RowLabelWrapper>
                    <RowItemLabel>{t('last_name')}</RowItemLabel>
                    <Mandatory>*</Mandatory>
                  </RowLabelWrapper>
                  <RowItemContainer>
                    <Input
                      value={basicInfo?.lastName}
                      onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                        evt.target.value.length <= 35 &&
                          alphaNumericNameWithSpaceAndHyphen(evt.target.value) &&
                          updateInfoOnFieldChange('lastName', evt.target.value as string);
                      }}
                      placeholder={t('last_name')}
                    />
                  </RowItemContainer>
                </FormSection>
                <FormSection marginBottom="18">
                  <RowLabelWrapper>
                    <RowItemLabel>{t('date_of_birth')}</RowItemLabel>
                  </RowLabelWrapper>
                  <RowItemContainer>
                    <Input
                      value={basicInfo?.dateOfBirth}
                      type="date"
                      // max="9999-12-31"
                      onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                        updateInfoOnFieldChange('dateOfBirth', evt.target.value as string);
                      }}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </RowItemContainer>
                </FormSection>
                <FormSection marginBottom="18">
                  <RowLabelWrapper>
                    <RowItemLabel>{t('phone_number')}</RowItemLabel>
                  </RowLabelWrapper>
                  <RowItemContainer>
                    <PhoneContainer>
                      <PhoneInput
                        defaultCountry="us"
                        value={basicInfo?.phone}
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        onChange={(phone: string, country: CountryIso2) => {
                          updateInfoOnFieldChange('phone', phone as string);
                        }}
                        disableDialCodePrefill
                      />
                    </PhoneContainer>
                  </RowItemContainer>
                </FormSection>

                <FormSection marginBottom="18">
                  <RowLabelWrapper>
                    <RowItemLabel>{t('date_format')}</RowItemLabel>
                  </RowLabelWrapper>
                  <RowItemContainer>
                    <Select
                      options={UserSettingDateFormat}
                      value={{ value: basicInfo?.id, label: basicInfo?.dateFormatPreference }}
                      onChange={(evt) => {
                        updateInfoOnFieldChange(
                          'dateFormatPreference',
                          toUpper(evt?.label as string)
                        );
                      }}
                      className="select-dropdown-items"
                      placeholder="Select..."
                    />
                  </RowItemContainer>
                </FormSection>

                <FormSection marginBottom="18">
                  <RowLabelWrapper>
                    <RowItemLabel isDisabled={true}>{t('email_address')}</RowItemLabel>
                    <Mandatory>*</Mandatory>
                  </RowLabelWrapper>
                  <RowItemContainer>
                    <Input
                      value={basicInfo?.email}
                      variant="disabled"
                      onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                        updateInfoOnFieldChange('email', evt.target.value.trim() as string);
                      }}
                      placeholder="Email"
                    />
                  </RowItemContainer>
                </FormSection>
              </BioLeftColulmn>
              <BioRightColumn>
                <FormSection marginBottom="18">
                  <RowLabelWrapper>
                    <RowItemLabel>{t('department')}</RowItemLabel>
                  </RowLabelWrapper>
                  <RowItemContainer>
                    <Input
                      value={basicInfo?.department}
                      onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                        updateInfoOnFieldChange('department', evt.target.value as string);
                      }}
                      placeholder={t('department')}
                    />
                  </RowItemContainer>
                </FormSection>
                <FormSection marginBottom="18">
                  <RowLabelWrapper>
                    <RowItemLabel>{t('bio')}</RowItemLabel>
                  </RowLabelWrapper>
                  <TextContainer>
                    <StyledTextArea
                      rows={8}
                      value={basicInfo?.bio}
                      placeholder={t('tell_us_about') as string}
                      onChange={(event) => {
                        updateInfoOnFieldChange('bio', event.target.value as string);
                      }}
                      style={{
                        height: 230,
                        resize: 'none',
                        paddingLeft: '12px',
                        paddingTop: '10px'
                      }}
                    />
                  </TextContainer>
                </FormSection>
                <AdjustForHeight>
                  <FormSection marginBottom="18">
                    <RowLabelWrapper>
                      <RowItemLabel>{t('time_format')}</RowItemLabel>
                    </RowLabelWrapper>
                    <RowItemContainer>
                      <Select
                        options={UserSettingTimeFormat}
                        value={{ value: basicInfo?.id, label: basicInfo?.timeFormatPreference }}
                        onChange={(evt) => {
                          updateInfoOnFieldChange('timeFormatPreference', evt?.label as string);
                        }}
                        className="select-dropdown-items"
                        placeholder="Select..."
                      />
                    </RowItemContainer>
                  </FormSection>
                </AdjustForHeight>
              </BioRightColumn>
            </BioDataBox>

            <SettingsFooter>
              <ButtonWrapper>
                <Button
                  variant="filled-primary"
                  size="small"
                  type="submit"
                  disabled={checkSaveDisable()}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!basicInfo) return;
                    updateBasicInfo(basicInfo)
                      .unwrap()
                      .then((res) => {
                        refetch();
                        ToastMsg({
                          heading: 'Success',
                          type: 'success',
                          message: 'Profile updated successfully'
                        });
                        setBasicInfo(res);
                        setBasicInfoTracked(res);
                      })
                      .catch(() => {
                        ToastMsg({
                          heading: 'Error',
                          type: 'error',
                          message: 'Error updating profile'
                        });
                      });
                  }}
                >
                  {t('save')}
                </Button>
              </ButtonWrapper>
            </SettingsFooter>
          </UserDataForm>
        )}
      </Container>
    </>
  );
};
